import { Box, Flex } from "@chakra-ui/layout"
import { useState, ReactEventHandler } from "react"
import { createPortal } from "react-dom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { chatsSelector, previewAttachmentId } from "src/recoil/atoms/chats"
import { MessageModel } from "src/recoil/atoms/chats/types"
import { brandingSelector } from "src/recoil/atoms/room"
import { isMobileDevice } from "src/util/browser"
import useIsVideo from "./utils/useIsVideo"
import { ChatCloseIcon } from "./_chatCloseIcon"
import useWindowSize from "../../../hooks/useWindowSize"
import useSizeImage from "./utils/useSizeImage"

interface ChatAttachmentProps {
  chat: Partial<MessageModel>
  hasMessage?: boolean
}

function ChatAttachment({ chat, hasMessage }: ChatAttachmentProps) {
  const { isVideo } = useIsVideo(chat.attachmentURL)
  const roomStyles = useRecoilValue(brandingSelector)
  const [previewId, setShowPreview] = useRecoilState(previewAttachmentId)
  
  const { screenSize } = useWindowSize()
  const screenIsLandscape = screenSize.width > screenSize.height

  const isMobile = isMobileDevice(screenSize)
  const [aspectRatio, setAspectRatio] = useState<`${number}/${number}`>("1/1")
  const { width, height } = useSizeImage()
  const setChat = useSetRecoilState(chatsSelector(chat.id))

  const onLoad: ReactEventHandler<HTMLImageElement> = e => {
    const { offsetWidth: w, offsetHeight: h } = e.currentTarget
    setAspectRatio(`${w}/${h}`)
    setChat({ attachmentLoadComplete: true })
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      marginBottom={hasMessage && "6px"}
      marginTop={isMobile && "6px"}
    >
      <img
        src={chat.attachmentThumbnailURL}
        alt="User uploaded"
        width="100%"
        height="auto"
        style={{
          borderRadius: "6px",
          overflow: "hidden",
          cursor: "pointer",
          maxHeight: "200px",
          width: "auto"
        }}
        onClick={e => {
          e.stopPropagation()
          setShowPreview(chat.id)
        }}
        onLoad={onLoad}
      />
      {previewId === chat.id &&
        createPortal(
          <Box
            pos="fixed"
            width="100%"
            height="100vh"
            background="rgba(0, 0, 0, 0.8)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onClick={() => {
              setShowPreview("")
            }}
          >
            <Box
              cursor="initial"
              pos="relative"
              aspectRatio={aspectRatio}
              maxWidth={width}
              maxHeight={height}
              onClick={e => e.stopPropagation()}
            >
              <ChatCloseIcon
                styleschat={roomStyles}
                isMobile={false}
                callback={() => setShowPreview("")}
                id={"ChatAttachmentPreviewClose"}
                isLarge
              />
              {isVideo ? (
                <video
                  src={chat.attachmentURL}
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    objectFit: "contain",
                    boxShadow: "0 0 15px #e2e8f0",
                    width: "100%",
                    height: "100%"
                  }}
                  autoPlay
                  controls
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <img
                  src={chat.attachmentURL}
                  alt="User uploaded"
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    objectFit: "contain",
                    boxShadow: "0 0 15px #e2e8f0",
                    width: "100%",
                    height: "100%"
                  }}
                  onClick={e => e.stopPropagation()}
                />
              )}
            </Box>
          </Box>,
          document.body
        )}
    </Flex>
  )
}

export default ChatAttachment
